import React from 'react';
import { actsAsOwner } from './ownership';
import { TaskStatus } from './taskWorkflow';
import { usesPermissionFlow, SubTaskPermissionType } from './permission';

function hasReplySubtask(task, query) {
  if (query?.replySubtaskId) return true;
  const queryId = String(query?.id || '');
  const queryText = String(query?.text || '').trim();
  const subtasks = Array.isArray(task?.subTask) ? task.subTask : [];
  return subtasks.some((subtask) => {
    if (subtask.permissionType !== SubTaskPermissionType.QUERY_RAISED) return false;
    if (queryId && String(subtask.permissionQueryId || '') === queryId) return true;
    return Boolean(queryText) && String(subtask.description || '').trim() === queryText;
  });
}

const QueryRoundsPanel = ({ task, onAddQuery, onCreateReply, embedded = false }) => {
  const isOwner = actsAsOwner(task);

  if (!usesPermissionFlow(task)) return null;

  const queries = Array.isArray(task.permissionQueries) ? task.permissionQueries : [];
  const isCancelled = task.status === TaskStatus.CANCELLED;

  const formatDate = (value) => {
    if (!value) return '---';
    return new Date(value).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className={embedded ? 'space-y-5' : 'bg-white rounded-lg shadow p-6 space-y-5'}>
      <div className="flex items-center justify-end gap-3">
        {!embedded && <h2 className="mr-auto text-lg font-semibold text-gray-800">Query rounds</h2>}
        {isOwner && !isCancelled && onAddQuery && (
          <button
            type="button"
            onClick={onAddQuery}
            className="shrink-0 px-4 py-2 bg-[#346C75] text-white font-medium text-sm rounded hover:bg-[#2a565e] transition-colors"
          >
            + Add query
          </button>
        )}
      </div>
      {queries.length === 0 ? (
        <p className="text-sm text-gray-500">No queries yet.</p>
      ) : (
        <ol className="space-y-2">
          {queries.map((row, index) => {
            const solved = Boolean(row.solvedAt);
            return (
              <li key={row.id || index} className="text-sm border rounded p-3 bg-gray-50">
                <div className="flex items-center gap-2">
                  <div className="font-medium text-gray-700">Query #{index + 1}</div>
                  {solved ? (
                    <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                      Solved
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">
                      Open
                    </span>
                  )}
                </div>
                <p className="text-gray-800 mt-1 whitespace-pre-wrap">{row.text}</p>
                <div className="text-xs text-gray-500 mt-1">
                  {formatDate(row.createdAt)}
                  {solved ? ` · Solved ${formatDate(row.solvedAt)}` : ''}
                </div>
                {/* Only for a query left without a reply, e.g. an older query or
                    one whose reply subtask failed to save. */}
                {isOwner &&
                  !solved &&
                  !isCancelled &&
                  onCreateReply &&
                  !hasReplySubtask(task, row) && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => onCreateReply(row, index)}
                      className="bg-[#346C75] text-white px-3 py-1 rounded text-sm hover:bg-[#2a565e]"
                    >
                      Create reply
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {isOwner && !isCancelled && (
        <p className="text-xs text-gray-500">
          Add query records the authority&apos;s query and creates the reply subtask that answers it.
          The query is marked solved when that reply subtask is accepted. Stage follows the
          subtask types.
        </p>
      )}
    </div>
  );
};

export default QueryRoundsPanel;
